import React from 'react';
import { motion } from 'framer-motion';
import { Wifi, Gift, Bell, ConciergeBell } from 'lucide-react';

const BookingBenefits = () => {
  const benefits = [
    { title: "Complimentary Wi-Fi", icon: Wifi },
    { title: "Exclusive Offers", icon: Gift },
    { title: "Personalize Booking Experience", icon: Bell },
    { title: "Priority Service", icon: ConciergeBell }
  ];

  const bgImage = "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=1920&q=80";

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      <div className="container-custom">
        <div className="flex flex-col lg:flex-row rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
          
          {/* Left Side - Image Container */}
          <div className="relative w-full lg:w-1/2 min-h-[400px] lg:min-h-full">
            <img 
              src={bgImage} 
              alt="Luxury Suite View" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Elegant overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-luxury-black/80 via-luxury-black/40 to-transparent" />
            
            {/* CTA button positioned over image on desktop */}
            <div className="absolute bottom-8 left-8 lg:bottom-12 lg:left-12">
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-medium tracking-wide transition-all duration-300 shadow-[0_10px_20px_rgba(249,115,22,0.3)] hover:shadow-[0_15px_30px_rgba(249,115,22,0.4)] hover:-translate-y-1"
              >
                Book Now
              </button>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="w-full lg:w-1/2 bg-luxury-black p-10 md:p-16 flex flex-col justify-center relative">
            {/* Subtle background pattern/glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />

            <div className="relative z-10">
              <span className="text-orange-400 font-medium tracking-[0.2em] uppercase text-xs mb-4 block">
                Direct Booking
              </span>
              <h2 className="text-3xl md:text-4xl text-white font-serif leading-tight mb-6">
                Book directly with us & explore all the special offers & new benefits you can enjoy
              </h2>
              <p className="text-gray-400 font-light mb-12 text-sm md:text-base leading-relaxed">
                You will have direct access to information about future promotions, food festivals, exclusive events, all that's new and happening at GPHUB Luxury Suites.
              </p>

              {/* Benefits Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center gap-4 group cursor-pointer"
                    >
                      <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-orange-500 group-hover:border-orange-500 transition-colors duration-300 shrink-0">
                        <Icon className="w-6 h-6 text-orange-400 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <span className="text-white/80 group-hover:text-white text-sm tracking-wider uppercase font-medium transition-colors duration-300">
                        {benefit.title}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default BookingBenefits;
